<template>
	<div class="content-page content-page--environment-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					<span
						class="content-page__title--detail"
						v-if="pageTitle"
						>{{ pageTitle }}</span
					>
				</h2>
			</div>
		</div>
		<div class="results-toolbar results-toolbar--variant-three">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
				placeholder="Search by action"
			/>
			<div class="results-toolbar__counter" v-if="!loading && hasResults">
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>
		<Spinner :processor="{ loading: loading }" />
		<ScrollContainer v-if="!loading && hasResults" data-test="pageContent">
			<Table
				:sourceObject="tableSource"
				@sort="updateDirection"
				@change="updatedPagination"
				:sortOptions="handleSort"
				class="table-container--environment-history"
				:isArrowUp="true"
			>
				<template #col-group>
					<colgroup>
						<!-- Date/Time -->
						<col class="table__col table__col--date-time" />

						<!-- Moved From -->
						<col class="table__col table__col--action" />

						<!-- Moved To -->
						<col class="table__col table__col--summary" />

						<!-- Changed By -->
						<col class="table__col table__col--changed-by" />
					</colgroup>
				</template>
				<template #body-timestamp="{ body }">
					<div class="environment-history-data">
						{{ fullDateString(body.timestamp) }}
						<div class="environment-history-data__text-emphasis">
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-actionDescription="{ body }">
					<div class="environment-history-data">
						<div class="environment-history-data__text-emphasis">
							{{ body.actionDescription }}
						</div>
					</div>
				</template>
				<template #body-summary="{ body }">
					<div class="environment-history-data">
						<div
							class="environment-history-data__text-log"
							v-html="body.summary"
						></div>
					</div>
				</template>
				<template #body-changedByUserAndRole="{ body }">
					<div class="environment-history-data">
						{{ body.changedByUserAndRole }}
					</div>
				</template>
			</Table>
		</ScrollContainer>

		<NoResults v-if="!hasResults && !loading" />
	</div>
</template>

<script>
import Table from '@/components/shells/table/table';
import HistoryFilters from '@/pages/global-history-screen-filters.vue';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
//;
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import DateTimeMixin from '@/mixins/datetime-mixin';
import { SET_ZONE_HISTORY_LOGS } from '@/store/modules/mutation-types.js';
import { mapState, mapGetters, mapActions } from 'vuex';

// libraries
import _ from 'lodash';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';
import { parseDateToUTC } from '@/utils/date/parseDate';

export default {
	name: 'EnvironmentHistory',
	mixins: [DateTimeMixin],
	components: {
		Table,
		ResultCounter,
		Spinner,
		Breadcrumbs,
		HistoryFilters,
		NoResults,
		ScrollContainer
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		SourceApplication: String,
		changedEntityId: [String, Number],
		recordStart: Number,
		maxCount: Number
	},
	data() {
		return {
			loading: false,
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: '',
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				SourceApplication: ['VueSupport'],
				entityType: ['CustomerActionModel'],
				changedEntityId: this.$route.params.changedEntityId,
				recordStart: 1,
				maxCount: 10
			},
			snippetsHistory: [],
			defaultUrlNames: [
				{
					url: `/environments`,
					name: 'Zone Manager'
				},
				{
					name: 'History'
				}
			],
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			currentItem: {},
			openTag: '<span class="environment-history-data__text-emphasis">',
			openTagFirstLetter:
				'<span class="environment-history-data__text-log__first-letter">',
			closeTag: '</span>'
		};
	},
	async created() {
		this.tableSource.headers = this.renderHeader();

		await this.getUsers({
			entityType: 'CustomerActionModel',
			changedEntityId: this.changedEntityId
		});
	},
	computed: {
		...mapState('historyLogs', ['users']),
		...mapGetters('customers', ['zoneHistoryLogs']),
		pageTitle() {
			const navbarTitle = _.get(
				this.tableSource,
				'source.navbarTitle',
				''
			);
			if (!navbarTitle) {
				return 'CCL Zone History';
			}
			return `CCL Zone History - ${navbarTitle}`;
		},
		hasUpdatePermission() {
			return this.validatePermission('environments.update');
		},
		hasResults() {
			return this.resultCounter > 0;
		},
		resultCounter() {
			return this.tableSource.source.totalCount;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		}
	},
	methods: {
		...mapActions('historyLogs', ['getUsers']),
		...mapActions('customers', {
			fetchZoneHistoryLogs: SET_ZONE_HISTORY_LOGS
		}),
		async fetchData() {
			this.loading = true;
			this.loadingSource = true;
			let filteredQuery = removeObjectEmptyValues(this.filters);
			if (filteredQuery.orderBy === 'changedByUserAndRole') {
				Object.assign(filteredQuery, { orderBy: 'ChangedByUserName' });
			}

			if (filteredQuery.minDatetime) {
				const minDatetime = parseDateToUTC(filteredQuery.minDatetime);
				Object.assign(filteredQuery, { minDatetime: minDatetime });
			}
			if (filteredQuery.maxDatetime) {
				const maxDatetime = parseDateToUTC(
					filteredQuery.maxDatetime,
					true
				);
				Object.assign(filteredQuery, { maxDatetime: maxDatetime });
			}

			//filteredQuery.DealershipGroupGuid = this.changedEntityId; TODO: Remove this comment when DealershipGroupGuid is implemented to loggin

			try {
				await this.fetchZoneHistoryLogs(filteredQuery).then(() => {
					this.parseLogsData(this.zoneHistoryLogs);
				});
			} finally {
				this.loading = false;
			}
		},
		renderHeader() {
			return [
				{
					key: 'timestamp',
					label: 'Date/Time',
					sorteable: true
				},
				{
					key: 'actionDescription',
					label: 'Action',
					sorteable: false
				},
				{
					key: 'summary',
					label: 'Summary',
					sorteable: false
				},
				{
					key: 'changedByUserAndRole',
					label: 'Changed By User - Role',
					sorteable: true
				}
			];
		},
		updateRoute(filters) {
			let queries = removePropertiesWithSameValues(
				filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);
			let path = `/environments/${this.changedEntityId}/history`;
			this.$router
				.push({
					path: path,
					query: queries
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
			this.filters.orderBy = sortValue.sortBy;
		},
		updatedPagination(obj) {
			this.filters.recordStart = obj.page;
			this.filters.maxCount = obj.countPerPage;
		},
		parseLogsData(logs) {
			let results = _.cloneDeep(logs);
			if (results?.items?.length > 0) {
				results.items.forEach(item => {
					item.changedByUserAndRole =
						item.changedByUserName + ' - ' + item.changedByUserRole;

					item.actionDescription = this.getActionDescription(item);
					item.summary = this.formatSummary(item);
				});
			}

			this.tableSource.source = results;
		},
		getActionDescription(item) {
			return item.data.items[0]?.Action ?? item.data.items[1].Action;
		},
		formatSummary(item) {
			this.currentItem = { ...item };
			let actions = {
				code: this.codeReleaseSummary,
				manual: this.manualMoveSummary,
				created: this.createScheduleSummary,
				rescheduled: this.rescheduleSummary,
				added: this.addedToScheduleSummary,
				removed: this.removeScheduleSummary,
				edited: this.editScheduleSummary,
				stopped: this.stoppedScheduleSummary,
				successful: this.successfulScheduleSummary,
				error: this.errorScheduleSummary
			};
			const keyAction = Object.keys(actions).find(key =>
				item.actionDescription.toLowerCase().includes(key)
			);
			const resultSummary = actions[keyAction]();
			return resultSummary;
		},
		getReleaseTypeText(type) {
			if (type == 'H') {
				return 'Hotfix Release';
			}
			return 'Spring Release';
		},
		codeReleaseSummary() {
			return 'Code passed, client moved to Prod';
		},
		manualMoveSummary() {
			const originZone = this.currentItem.data.items[0].ZoneName;
			const destinationZone = this.currentItem.data.items[1].ZoneName;

			return `${this.openTagFirstLetter}Move${this.closeTag} from ${this.openTag}${originZone}${this.closeTag} to ${this.openTag}${destinationZone}${this.closeTag}`;
		},
		createScheduleSummary() {
			const index = 1;

			let results = [];

			const item = this.currentItem.data.items[index];
			if (item) {
				const { ReleaseType, Comment, ZoneName } = item;

				results.push(
					`${this.openTagFirstLetter}Scheduled${this.closeTag} move`
				);
				results.push(`to ${this.openTag}${ZoneName}${this.closeTag},`);

				if (ReleaseType) {
					results.push(`${this.openTag}ReleaseType:${this.closeTag}`);
					const release = this.getReleaseTypeText(ReleaseType);
					results.push(release);
				}
				if (Comment) {
					results.push(`${this.openTag}Comments:${this.closeTag}`);
					results.push(Comment);
				}

				return results.join(' ');
			}
			return '';
		},
		rescheduleSummary() {
			const newObjIndex = 1;
			let results = [];

			const item = this.currentItem.data.items[newObjIndex];

			if (item) {
				const { ReleaseType, Comment, ZoneName, ScheduleDate } = item;

				results.push(
					`${this.openTagFirstLetter}Rescheduled${this.closeTag} move`
				);

				if (ZoneName) {
					results.push(
						`to ${this.openTag}${ZoneName}${this.closeTag},`
					);
				}

				if (ScheduleDate) {
					const dateChanged = this.shortDateString(ScheduleDate);
					results.push(
						`on ${this.openTag}${dateChanged}${this.closeTag},`
					);
				}

				if (ReleaseType) {
					results.push(`${this.openTag}ReleaseType:${this.closeTag}`);
					const release = this.getReleaseTypeText(ReleaseType);
					results.push(release);
				}
				if (Comment) {
					results.push(`${this.openTag}Comments:${this.closeTag}`);
					results.push(Comment);
				}

				return results.join(' ');
			}
			return '';
		},
		addedToScheduleSummary() {
			const newObjIndex = 1;
			const item = this.currentItem.data.items[newObjIndex];

			if (item) {
				return this.parseAddedRemoved('added', item);
			}
			return '';
		},
		removeScheduleSummary() {
			const oldObjIndex = 0;
			const item = this.currentItem.data.items[oldObjIndex];

			if (item) {
				return this.parseAddedRemoved('removed', item);
			}
			return '';
		},
		parseAddedRemoved(action, item) {
			let results = [];

			const actionObj = {
				added: `${this.openTagFirstLetter}Added${this.closeTag} to the scheduled move to`,
				removed: `${this.openTagFirstLetter}Removed${this.closeTag} from a scheduled move to`
			};
			if (action) {
				const verbiageByAction = actionObj[action];
				results.push(verbiageByAction);
				results.push(`${this.openTag}${item.ZoneName}${this.closeTag}`);

				const { ScheduleDate, ReleaseType, Comment } = item;

				results.push(`on `);
				if (ScheduleDate) {
					const dateChanged = this.shortDateString(
						this.currentItem.timestamp
					);
					results.push(
						`${this.openTag}${dateChanged}${this.closeTag},`
					);
				}

				if (ReleaseType) {
					results.push(`${this.openTag}ReleaseType:${this.closeTag}`);
					const releaseType = this.getReleaseTypeText(
						item.ReleaseType
					);
					results.push(releaseType);
				}
				if (Comment) {
					results.push(`${this.openTag}Comments:${this.closeTag}`);
					results.push(item.Comment);
				}

				return results.join(' ');
			}
			return '';
		},
		editScheduleSummary() {
			let results = [];

			const newObjIndex = 1;

			const { ScheduleDate, ZoneName } = this.currentItem.data.items[
				newObjIndex
			];

			if (ZoneName) {
				results.push(
					`${this.openTagFirstLetter}The${this.closeTag} scheduled move`
				);
				results.push(`to ${this.openTag}${ZoneName}${this.closeTag}`);
			}
			results.push(`on `);
			if (ScheduleDate) {
				const dateChanged = this.shortDateString(
					this.currentItem.timestamp
				);
				results.push(`${this.openTag}${dateChanged}${this.closeTag}`);
			}

			const changes = _.get(this.currentItem.fieldChanges, 'items', []);

			if (changes.length > 0) {
				results.push('has the following parameters changed:');
				changes.forEach(item => {
					if (
						item.ChangedPropertyName !== 'ZoneId' &&
						item.ChangedPropertyName !== 'Comment'
					) {
						results.push(`${this.openTag}`);
						results.push(item.ChangedPropertyName);
						results.push(`: ${this.closeTag}`);

						if (item.ChangedPropertyName === 'ReleaseType') {
							const releaseType = this.getReleaseTypeText(
								item.NewValue
							);
							results.push(releaseType);
						} else if (
							item.ChangedPropertyName === 'ScheduleDate'
						) {
							const dateChanged = this.shortDateString(
								item.NewValue
							);
							results.push(dateChanged);
						} else {
							results.push(item.NewValue);
						}
					}
				});
				const fieldComments = changes.find(
					item => item.ChangedPropertyName === 'Comment'
				);
				if (!_.isEmpty(fieldComments)) {
					results.push(`${this.openTag}Comments:${this.closeTag}`);
					results.push(`${fieldComments.NewValue}`);
				}

				return results.join(' ');
			}
			return '';
		},
		stoppedScheduleSummary() {
			const newObjIndex = 1;
			let results = [];
			const {
				ReleaseType,
				ZoneName,
				ActionComment
			} = this.currentItem.data.items[newObjIndex];

			results.push(
				`${this.openTagFirstLetter}The${this.closeTag} scheduled move to`
			);
			results.push(
				`${this.openTag}${ZoneName}${this.closeTag} was stopped,`
			);
			if (ReleaseType) {
				results.push(`${this.openTag}ReleaseType:${this.closeTag}`);
				const releaseType = this.getReleaseTypeText(ReleaseType);
				results.push(releaseType);
			}
			if (ActionComment) {
				results.push(`${this.openTag}Comments:${this.closeTag}`);
				results.push(ActionComment);
			}
			return results.join(' ');
		},
		successfulScheduleSummary() {
			let results = [];

			const {
				Status,
				ScheduleDate,
				Action
			} = this.currentItem.data.items[1];

			if (
				Status === 'SUCCESS' ||
				(Action && Action.toLowerCase().includes('successful'))
			) {
				results.push(
					`${this.openTagFirstLetter}Scheduled${this.closeTag} move`
				);

				if (ScheduleDate) {
					const dateChanged = this.shortDateString(ScheduleDate);
					results.push(
						`on ${this.openTag}${dateChanged}${this.closeTag}`
					);
				}
				results.push('was successful.');
				return results.join(' ');
			}
			return '';
		},
		errorScheduleSummary() {
			let results = [];
			let firstWord = '';
			let restOfActionComment = '';

			const {
				Status,
				ActionComment,
				ScheduleDate
			} = this.currentItem.data.items[0];

			if (Status === 'ERROR') {
				results.push(
					`${this.openTagFirstLetter}The${this.closeTag} scheduled move`
				);

				if (ScheduleDate) {
					const dateChanged = this.shortDateString(ScheduleDate);
					results.push(
						`on ${this.openTag}${dateChanged}${this.closeTag}`
					);
				}
				results.push(`got an error.`);

				if (ActionComment) {
					firstWord = ActionComment.charAt(0);
					restOfActionComment = ActionComment.slice(1);
				}

				if (ActionComment) {
					results.push(
						`${this.openTagFirstLetter}${firstWord}${this.closeTag}${restOfActionComment}`
					);
				} else {
					results.push(
						`${this.openTagFirstLetter}There${this.closeTag} was an error during the automated scheduling process.`
					);
				}
				return results.join(' ');
			}
			return '';
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(newquery) {
				this.updateRoute(newquery);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchData();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * Environment History
**/

.results-toolbar {
	width: 100%;
}

::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 280px;
	}
}

::v-deep .dropdown {
	width: 240px;
}
</style>

<style lang="scss">
/*------------------------------------*\
  Environment History Table
\*------------------------------------*/

.table-container--environment-history {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		@include mq($from: md) {
			&--date-time {
				width: 95px;
			}
			&--action {
				width: 110px;
			}
			&--summary {
				width: 200px;
			}
			&--changed-by {
				width: 130px;
			}
		}
	}
}

/*------------------------------------*\
  Environment History Table - Data
\*------------------------------------*/

.environment-history-data {
	&__text-emphasis {
		@include body-text('regular-emphasis');
		margin-top: $gutter-sm;
		text-transform: capitalize;
	}
	&__text-action {
		@include body-text('regular-emphasis');
	}
	&__text-log {
		margin-top: $gutter-sm;
		word-break: break-all;
		text-transform: lowercase;
		&__first-letter {
			text-transform: capitalize;
		}
	}

	&__text-log {
		margin-top: 0;
	}
}
</style>
